import { useMemo } from 'react'

export type OlarkVisitorCustomFields = {
  internalCustomerId: string
  carrierId?: string
  companyName?: string
}

export type OlarkCommandMap = {
  'api.chat.sendNotificationToOperator': { body: string }
  'api.chat.updateVisitorStatus': { snippet: string }
  'api.visitor.updateCustomFields': OlarkVisitorCustomFields
  'api.visitor.updateEmailAddress': { emailAddress?: string }
  'api.visitor.updateFullName': { fullName: string }
  'api.visitor.updatePhoneNumber': { phoneNumber: string }
}

export type OlarkTypedCommand = keyof OlarkCommandMap

export type OlarkTypedFn = <K extends OlarkTypedCommand>(
  command: K,
  options: OlarkCommandMap[K],
) => void

export type OlarkOperatorCommandEvent = {
  command: { name: string }
}

export type OlarkFn = OlarkTypedFn & {
  (command: 'api.box.expand'): void
  (command: 'api.box.hide', hidden: boolean): void
  (command: 'api.box.onShrink', callback: () => void): void
  (command: 'api.box.show'): void
  (command: 'api.box.shrink'): void
  (command: 'api.box.onExpand', callback: () => void): void
  (command: 'api.chat.onMessageToOperator', callback: () => void): void
  (command: 'api.chat.onMessageToVisitor', callback: () => void): void
  (command: 'api.chat.onBeginConversation', callback: () => void): void
  (
    command: 'api.chat.onCommandFromOperator',
    callback: (event: OlarkOperatorCommandEvent) => void,
  ): void
  (command: 'api.chat.onReady', callback: () => void): void
  (command: 'configure', key: string, value: unknown): void
  (command: 'identify', siteId: string | undefined): void
}

export type OlarkVisitorConversationSync = {
  carrierId?: string
  company?: string
  email?: string
  linkLabel?: string
  name?: string
  phoneNumber?: string
  userId: string
  viewAsLink?: string
}

export const createOlarkCommands = (
  getOlark: () => OlarkTypedFn | undefined,
) => {
  const run = <K extends OlarkTypedCommand>(
    command: K,
    options: OlarkCommandMap[K],
  ) => {
    const olark = getOlark()
    if (!olark) return
    olark(command, options)
  }

  const sendOperatorNotification = (message: string) => {
    run('api.chat.sendNotificationToOperator', { body: message })
  }

  const updateVisitorCustomFields = (fields: OlarkVisitorCustomFields) => {
    run('api.visitor.updateCustomFields', fields)
  }

  const updateVisitorEmailAddress = (emailAddress?: string) => {
    run('api.visitor.updateEmailAddress', { emailAddress })
  }

  const updateVisitorFullName = (fullName: string) => {
    run('api.visitor.updateFullName', { fullName })
  }

  const updateVisitorPhoneNumber = (phoneNumber: string) => {
    run('api.visitor.updatePhoneNumber', { phoneNumber })
  }

  const updateVisitorStatus = (snippet: string) => {
    run('api.chat.updateVisitorStatus', { snippet })
  }

  const syncVisitorOnConversationStart = ({
    carrierId,
    company,
    email,
    linkLabel,
    name,
    phoneNumber,
    userId,
    viewAsLink,
  }: OlarkVisitorConversationSync) => {
    updateVisitorCustomFields({
      internalCustomerId: userId,
      ...(carrierId && { carrierId }),
      ...(company && { companyName: company }),
    })

    updateVisitorEmailAddress(email)

    if (name) {
      updateVisitorFullName(name)
    }

    if (phoneNumber) {
      updateVisitorPhoneNumber(phoneNumber)
    }

    viewAsLink && updateVisitorStatus(viewAsLink)
    viewAsLink && sendOperatorNotification(`${linkLabel}: ${viewAsLink}`)
  }

  return {
    sendOperatorNotification,
    syncVisitorOnConversationStart,
    updateVisitorCustomFields,
    updateVisitorEmailAddress,
    updateVisitorFullName,
    updateVisitorPhoneNumber,
    updateVisitorStatus,
  }
}

type OlarkCommands = ReturnType<typeof createOlarkCommands>

const getWindowOlark = (): OlarkTypedFn | undefined =>
  window.olark as OlarkTypedFn | undefined

export const useOlarkCommands = (): OlarkCommands => {
  return useMemo(() => createOlarkCommands(getWindowOlark), [])
}
